// The two naming writes' state (#6566): a machine's rename and a server's name. The pure
// half is `./fleet-rename` (bodies, toasts, who is offered what); this half sends, holds
// the one in-flight flag and the API's refusal, toasts what the server did, and re-reads.
//
// EVERY WRITE RE-READS: `onWritten` refetches the roster, and nothing patches a row from
// the echo (`apps/dashboard/CLAUDE.md` § Boundary). A double submit while one is in flight
// is dropped, so one Save is one request.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { errorReason } from './api';
import { renameBox } from './box-lifecycle';
import {
  generatedName,
  renameBody,
  renameToast,
  serverLabelBody,
  serverLabelToast,
  type ToastInput,
} from './fleet-rename';
import type { UtilBox } from './fleetUtilization';
import { setServerLabel } from './server-label';

export type NameWrite = {
  readonly pending: () => boolean;
  /** The API's refusal (its own `detail`), or null. */
  readonly error: () => string | null;
  /** Send what the field holds; the body builder trims it and maps blank to `null`. */
  readonly save: (text: string) => void;
  /** Drop a stale refusal, as a reopened form does. */
  readonly reset: () => void;
};

type WriteInput = {
  readonly token: string | null;
  readonly notify: (message: string, tone?: 'success' | 'error') => void;
  /** Re-read the roster behind the control. */
  readonly onWritten: () => void;
  /** The write landed: the caller closes its form. */
  readonly onDone: () => void;
};

function createNameWrite(
  input: WriteInput & {
    readonly failed: string;
    /** The send and its toast, or null when there is nothing to send. */
    readonly plan: (text: string) => { send: () => Promise<unknown>; toast: ToastInput } | null;
  },
): NameWrite {
  const [pending, setPending] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  return {
    pending,
    error,
    reset: () => setError(null),
    save: (text) => {
      if (pending()) return;
      const plan = input.plan(text);
      if (plan === null) return;
      setPending(true);
      setError(null);
      plan
        .send()
        .then(() => {
          input.onDone();
          input.notify(t(plan.toast.key, plan.toast.vars));
          input.onWritten();
        })
        .catch((e: unknown) => setError(errorReason(e, t(input.failed))))
        .finally(() => setPending(false));
    },
  };
}

/** `PATCH /v1/runners/:id`: the trimmed name, or `null` to go back to the generated one. */
export function createMachineRenameWrite(
  input: WriteInput & {
    /** The machine the form renames; null (the panel shut) sends nothing. */
    readonly box: () => UtilBox | null;
  },
): NameWrite {
  return createNameWrite({
    ...input,
    failed: 'fleet.panel.rename_failed',
    plan: (text) => {
      const box = input.box();
      if (box === null) return null;
      const body = renameBody(text);
      return {
        send: () => renameBox(box.id, body.display_name, input.token),
        toast: renameToast(body, generatedName(box)),
      };
    },
  });
}

/**
 * `PUT /v1/runners/server-label` for the server group `groupKey`, off the UNFILTERED read
 * (`all`): every own-org machine the read places there (`serverLabelBody`).
 */
export function createServerNameWrite(
  input: WriteInput & {
    readonly all: () => readonly UtilBox[];
    readonly groupKey: () => string;
  },
): NameWrite {
  return createNameWrite({
    ...input,
    failed: 'fleet.machines.server_name_failed',
    plan: (text) => {
      const body = serverLabelBody(input.all(), input.groupKey(), text);
      if (body === null) return null;
      return {
        send: () => setServerLabel(body.runner_ids, body.server_label, input.token),
        toast: serverLabelToast(body),
      };
    },
  });
}
